/**
 * An advertising slot.
 *
 * PLACEMENT RULES THIS COMPONENT ENFORCES
 * ---------------------------------------
 * Visible without being in the way, which in practice means a short list of
 * things it deliberately is NOT:
 *
 *   - never fixed, sticky or floating. An overlay that follows the page is the
 *     definition of intrusive on a phone, and it steals the bottom of the
 *     viewport where the thumb lives.
 *   - never an interstitial, and never between a tap and its result.
 *   - never sized after load. The height is reserved up front, so content below
 *     does not jump when the creative arrives — layout shift is the single most
 *     irritating ad behaviour on mobile.
 *   - never dressed as content. A dashed edge, a muted ground and a permanent
 *     "Ad" label keep it clearly separate from a leaderboard row, so nobody
 *     taps it thinking it is a ranked product.
 *
 * It sits IN the scroll flow: you meet it while scrolling, it occupies its own
 * space, and it leaves when you scroll past.
 */

export interface AdSlotProps {
  /** Identifies the position to whatever fills it, e.g. "leaderboard-inline-1". */
  slot: string;
  /**
   * Reserved height in CSS pixels, mobile and desktop.
   *
   * Both are declared so the box is the right size before anything loads, on
   * either breakpoint.
   */
  height?: { mobile: number; desktop: number };
  /** Rendered inside the reserved box. Nothing means the empty placeholder. */
  children?: React.ReactNode;
}

export function AdSlot({
  slot,
  height = { mobile: 100, desktop: 120 },
  children,
}: AdSlotProps): React.JSX.Element {
  return (
    <aside
      className="ad-slot"
      data-slot={slot}
      // Reserved before load, so nothing below it moves later.
      style={
        {
          '--ad-h-mobile': `${height.mobile}px`,
          '--ad-h-desktop': `${height.desktop}px`,
        } as React.CSSProperties
      }
      aria-label="Advertisement"
    >
      <span className="ad-tag">Ad</span>
      <div className="ad-body">
        {children ?? <span className="ad-empty">Advertise here</span>}
      </div>
    </aside>
  );
}
